<template>
    <CustomDialog v-model="show">
        <template #header>
            <div class="text-lg font-medium text-gray-370 dark:text-white uppercase">
                Create Provider
            </div>
        </template>

        <div
            class="max-mob:px-4 px-7.5 py-5 w-99.5 max-mob:w-full"
            @keyup.enter="submitForm"
        >
            <div class="mb-3 text-gray-370 dark:text-white text-xs font-medium">
                Provider Name
            </div>

            <FormInput
                v-model="formData.name.value"
                v-model:valid="formData.name.valid"
                :rules="rules.default"
                placeholder="Type Provider Name"
                autoFocus
            />

            <div class="pt-5 mb-3 text-gray-370 dark:text-white text-xs font-medium">
                Choose Type
            </div>

            <SlidersBtn
                v-model:activeTab="typeId"
                v-model:sipOptions="tcpaShieldStore.providerTypes"
                width="max-mob:w-full"
            />
        </div>

        <template #footer>
            <div class="flex items-center justify-end w-full gap-x-5">
                <TextBtn @click="closeDialog">
                    Cancel
                </TextBtn>

                <PrimaryBtn
                    :loading="tcpaShieldStore.actionLoading"
                    @click="submitForm"
                >
                    Create
                </PrimaryBtn>
            </div>
        </template>
    </CustomDialog>
</template>

<script setup>
import {
    computed, reactive, ref, watch,
} from 'vue'

import CustomDialog from '@/components/dialogs/CustomDialog.vue'
import FormInput from '@/components/inputs/FormInput.vue'
import PrimaryBtn from '@/components/buttons/PrimaryBtn.vue'
import TextBtn from '@/components/buttons/TextBtn.vue'
import SlidersBtn from '@/components/buttons/SlidersBtn.vue'

import { checkFormValidity, collectFormData } from '@/helpers/app-helper'

import { useTcpaShieldStore } from '@/store/call-tracking/suppressionList/tcpaShieldStore'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
})

const emits = defineEmits(['update:modelValue'])

const tcpaShieldStore = useTcpaShieldStore()

const formData = reactive({
    name: {
        value: null,
        valid: true,
    },
})

const typeId = ref(null)

const rules = reactive({
    default: [],
})

const show = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emits('update:modelValue', value)
    },
})

function closeDialog() {
    show.value = false
}

async function submitForm() {
    await setRules()

    const valid = checkFormValidity(formData)

    if (valid) {
        createTcpaShield()
    }
}

function setRules() {
    rules.default = [
        (v) => !!v || 'This field is required',
    ]
}

async function createTcpaShield() {
    const { success } = await tcpaShieldStore.goToCreateTcpaShield({
        ...collectFormData(formData),
        type_id: typeId.value,
        credentials: getCredentials(),
    })

    if (success) {
        closeDialog()
    }
}

function getCredentials() {
    const foundType = tcpaShieldStore.providerTypes.find((type) => type.id === typeId.value)

    if (!foundType) {
        return {}
    }

    return foundType.credentials.reduce((credentials, credentialsName) => {
        credentials[credentialsName] = null

        return credentials
    }, {})
}

function resetFormData() {
    Object.keys(formData).forEach((key) => {
        formData[key].value = null
        formData[key].valid = true
    })

    rules.default = []

    typeId.value = tcpaShieldStore.providerTypes.length ? tcpaShieldStore.providerTypes[0].id : null
}

watch(show, (value) => {
    if (!value) {
        resetFormData()
    }
})

watch(() => tcpaShieldStore.providerTypes, (types) => {
    if (types.length) {
        typeId.value = tcpaShieldStore.providerTypes[0].id
    }
}, { immediate: true, deep: true })
</script>
